import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ToolSidebar from '../../utils/ToolSideBar';
import { fetchApprovals } from '../../../services/approvalApi';
import '../../../css/superuser/StaffDirectoryPage.css';
import '../../../css/staff/InternalWorkspace.css';

const tabs = [
  ['needs_my_approval', 'Needs My Approval'],
  ['submitted', 'Submitted by Me / My Team'],
  ['all', 'All Visible'],
  ['approved', 'Approved'],
  ['declined', 'Declined'],
  ['completed', 'Completed'],
];

function SummaryCard({ label, value }) {
  return <article className="approval-summary-card"><span>{label}</span><strong>{value || 0}</strong></article>;
}

function ApprovalSkeletons() {
  return <div className="approval-list" aria-label="Loading approvals">{[0, 1, 2].map((item) => <div className="approval-card approval-skeleton" key={item} />)}</div>;
}

function ApprovalCard({ approval }) {
  return (
    <article className="approval-card">
      <div className="approval-card-head">
        <div>
          <span>{approval.request_type_label}</span>
          <h2>{approval.title}</h2>
        </div>
        <span className={`approval-priority priority-${approval.priority}`}>{approval.priority_label}</span>
      </div>
      <p>{approval.description}</p>
      <div className="approval-meta-grid">
        <div><span>From</span><strong>{approval.requesting_team?.name || approval.requester?.name || 'SCD'}</strong></div>
        <div><span>To</span><strong>{approval.target_team?.name || approval.current_approver?.name || 'Approver queue'}</strong></div>
        <div><span>Project</span><strong>{approval.project?.name || 'No project'}</strong></div>
        <div><span>Status</span><strong><span className={`approval-status status-${approval.decision_status}`}>{approval.needs_my_decision ? 'Needs Your Approval' : approval.decision_status_label}</span></strong></div>
        <div><span>Fulfillment</span><strong>{approval.fulfillment_status_label}</strong></div>
        <div><span>Submitted</span><strong>{new Date(approval.created_at).toLocaleDateString()}</strong></div>
      </div>
      <Link className="team-card-action" to={`/approvals/${approval.id}`}>Review Request</Link>
    </article>
  );
}

export default function ApprovalsPage() {
  const [approvals, setApprovals] = useState([]);
  const [meta, setMeta] = useState({ summary: {}, categories: [] });
  const [tab, setTab] = useState('needs_my_approval');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchApprovals({ tab, category });
      setApprovals(data.results || []);
      setMeta(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to load approvals.');
    } finally {
      setLoading(false);
    }
  }, [tab, category]);

  useEffect(() => { loadApprovals(); }, [loadApprovals]);

  const emptyText = tab === 'needs_my_approval' ? 'Nothing needs your approval right now.' : 'No approvals match this view.';

  return (
    <div className="staff-shell">
      <ToolSidebar active="approvals" />
      <main className="staff-page internal-page approvals-page">
        <header className="staff-header internal-hero">
          <span>{meta.eyebrow || 'SCD OPERATIONS'}</span>
          <h1>{meta.heading || 'Approvals'}</h1>
          <p>{meta.supporting_text || 'Review requests involving your delivery teams.'}</p>
        </header>

        <section className="approval-summary-grid">
          <SummaryCard label="Pending" value={meta.summary?.pending} />
          <SummaryCard label="Needs My Decision" value={meta.summary?.needs_my_decision} />
          <SummaryCard label="Waiting on Others" value={meta.summary?.waiting_on_others} />
          <SummaryCard label="Completed This Month" value={meta.summary?.completed_this_month} />
        </section>

        <section className="staff-card approval-toolbar">
          <div className="approval-tabs">
            {tabs.map(([value, label]) => <button key={value} type="button" className={tab === value ? 'active' : ''} onClick={() => setTab(value)}>{label}</button>)}
          </div>
          <select value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Approval category">
            <option value="">All categories</option>
            {(meta.categories || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </section>

        <section className="staff-card approval-list-card">
          {loading ? <ApprovalSkeletons /> : error ? <p className="staff-empty inline team-error">{error}</p> : approvals.length === 0 ? <p className="staff-empty inline">{emptyText}</p> : <div className="approval-list">{approvals.map((approval) => <ApprovalCard key={approval.id} approval={approval} />)}</div>}
        </section>
      </main>
    </div>
  );
}